import { useState } from "react";
import { FaFilePdf, FaDownload } from "react-icons/fa";
import { useGetDeviceConnectionInfoPdfReportMutation } from "../../../store/storageRoles/reportsApi";
import useAuth from "../../../Hooks/useAuth";
import useReportFirms from "../../../Hooks/useReportFirms";
import ReportLayout from "../../../Layout/Reports/ReportLayout";
import ReportFilterCard from "../../../Components/Reports/ReportFilterCard";
import ReportLoadingState from "../../../Components/Reports/ReportLoadingState";
import ReportErrorState from "../../../Components/Reports/ReportErrorState";
import ReportEmptyState from "../../../Components/Reports/ReportEmptyState";
import ReportPdfPreview, {
  downloadPdfFromBase64,
} from "../../../Components/Reports/ReportPdfPreview";
import { FirmDropdown, NumberInput } from "../../../Components/atoms";

/**
 * CİHAZ BAĞLANTI BİLGİLERİ RAPORU — Excel/tablo yerine PDF.
 *
 * Rapor artık sunucuda PDF olarak üretilir (bkz.
 * `report-device-pdf.controller.js` → `device-connection.aggregator.js` →
 * `device-connection-report-pdf-html-generator.js`). Sayfa yalnızca filtreyi
 * toplayıp base64 PDF'i önizler ve indirtir.
 *
 * VERİ KAYNAĞI DEĞİŞMEDİ: sunucu aynı aggregation'ı çalıştırıyor, filtreler de
 * aynı ({ hour, firmId }). Tabloda client'ın hesapladığı "kopukluk süresi"
 * artık sunucuda üretiliyor — PDF'i client hesaplayamaz.
 *
 * Yerleşim `FirmPerformanceUnifiedReportPage` desenidir: `showExcel={false}`
 * + başlıkta PDF indirme butonu + solda filtre kartı + sağda PDF önizleme.
 */
export default function ReportDeviceConnectionInfo() {
  const { auth } = useAuth();
  const token = auth.accessToken;

  const [hour, setHour] = useState(1);
  const [firm, setFirm] = useState(null);

  const { firms, isLoading: firmDataIsLoading } = useReportFirms(token);

  const [getReport, { data, isLoading, error, reset }] =
    useGetDeviceConnectionInfoPdfReportMutation();

  // Saat zorunlu ve pozitif olmalı — sunucu da ayrıca reddeder, ama
  // butonu kapatmak boşuna bir tur PDF üretiminden ucuz.
  const canSearch = Number(hour) > 0;

  const handleSearch = () => {
    if (!canSearch) return;
    getReport({
      token,
      hour: Number(hour),
      // "Tüm Firmalar" seçimi filtresiz demektir; sunucu boş firmId'yi
      // "depo dışındaki tüm firmalar" olarak yorumlar.
      firmId: firm && firm._id !== "all" ? firm._id : "",
    });
  };

  const handleClearFilters = () => {
    setHour(1);
    setFirm(null);
    reset?.();
  };

  const report = data?.data;
  const pdfBase64 = report?.pdfBase64;

  const errorMessage =
    error?.data?.message || error?.error || "Rapor oluşturulurken bir hata oluştu.";

  const headerActions = pdfBase64 ? (
    <button
      onClick={() => downloadPdfFromBase64(pdfBase64, report?.fileName)}
      title="PDF İndir"
      className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 active:bg-red-800 transition-colors text-xs font-SemiBold"
    >
      <FaFilePdf className="w-4 h-4" />
      <FaDownload className="w-3 h-3" />
      <span className="hidden sm:inline">PDF İndir</span>
    </button>
  ) : null;

  const activeFilters = [
    { label: "Eşik", value: `${hour} saat` },
    { label: "Firma", value: firm && firm._id !== "all" ? firm.firmName : "Tümü" },
  ];

  return (
    <ReportLayout
      title="Cihaz Bağlantı Raporu"
      showExcel={false}
      headerActions={headerActions}
      sidebar={
        <ReportFilterCard
          title="Cihaz Bağlantı Filtreleme"
          description="Kaç saattir bağlanmayan cihazların listeleneceğini seçin"
          showDatePickers={false}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={isLoading}
          searchDisabled={!canSearch}
          searchDisabledMessage="Saat değeri 0'dan büyük olmalıdır"
          searchButtonText="Raporu Getir"
          clearButtonText="Temizle"
          activeFilters={activeFilters}
        >
          <div className="grid grid-cols-1 gap-4">
            <NumberInput
              label="Saat"
              placeholder="Saat girin..."
              value={hour}
              onChange={(value) => setHour(value)}
              min={1}
            />

            <FirmDropdown
              value={firm}
              onChange={(selected) =>
                setFirm(!selected || selected._id === "all" ? null : selected)
              }
              firms={firms}
              loading={firmDataIsLoading}
            />
          </div>
        </ReportFilterCard>
      }
    >
      {isLoading && (
        <ReportLoadingState
          title="Cihaz Bağlantı Raporu Hazırlanıyor"
          description="PDF üretiliyor, lütfen bekleyin..."
          progressText="Veriler işleniyor..."
          skeletonRows={6}
          skeletonColumns={4}
        />
      )}

      {!isLoading && error && (
        <ReportErrorState title="Hata Oluştu" message={errorMessage} icon="warning" />
      )}

      {!isLoading && !error && pdfBase64 && (
        <ReportPdfPreview
          pdfBase64={pdfBase64}
          title={`${report?.firmName || "Tüm Firmalar"} — ${
            report?.deviceCount ?? 0
          } bağlantısız cihaz`}
        />
      )}

      {!isLoading && !error && !pdfBase64 && (
        <ReportEmptyState
          title="Rapor Bekleniyor"
          message="Saat eşiğini (ve isterseniz firmayı) seçip 'Raporu Getir' butonuna basın."
          icon="document"
        />
      )}
    </ReportLayout>
  );
}
